import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface PageHeaderContextType {
    title: string | null;
    subtitle: React.ReactNode | null;
    setTitle: (title: string | null) => void;
    setSubtitle: (subtitle: React.ReactNode | null) => void;
}

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(undefined);

export const usePageHeader = (): PageHeaderContextType => {
    const context = useContext(PageHeaderContext);
    if (context === undefined) {
        throw new Error('usePageHeader must be used within a PageHeaderProvider');
    }
    return context;
};

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [title, setTitle] = useState<string | null>(null);
    const [subtitle, setSubtitle] = useState<React.ReactNode | null>(null);
    const setTitleStable = useCallback((value: string | null) => setTitle(value), []);
    const setSubtitleStable = useCallback(
        (value: React.ReactNode | null) => setSubtitle(value),
        []
    );
    const value = useMemo<PageHeaderContextType>(
        () => ({
            title,
            subtitle,
            setTitle: setTitleStable,
            setSubtitle: setSubtitleStable
        }),
        [title, subtitle, setTitleStable, setSubtitleStable]
    );
    return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
};
